// U5 holding reply: the small day picker above the holding draft ("Reply by: Thu 22 · Fri 23 …")
// and a way to take the holding reply off the card. Picking a day swaps the day's words in the
// text on screen (the owner's edits are kept); the server refuses when the text no longer names
// the old day, and says so. Typing while the change is on its way wins: the editor only takes the
// server's text when it still holds what was sent. When the text and the picked day disagree, a
// calm note says so (and the send won't arm until they match).
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { HOLDING_BLOCK_ID } from "@shared/unstick";
import { toast } from "sonner";
import { isApiError } from "@web/lib/api";
import { cn } from "@web/lib/cn";
import { acceptCard } from "@web/lib/feedQueries";
import { unstickApi, unstickKeys, useHoldingReply } from "@web/lib/unstickApi";
import { afterDayChange, holdingDayNote } from "./holdingReply";

export function HoldingDayPicker({
  cardId,
  value,
  onChange,
  readOnly,
}: {
  cardId: string;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const holding = useHoldingReply(cardId, true).data?.holding ?? null;
  const [busy, setBusy] = useState(false);
  const [keptNote, setKeptNote] = useState<string | null>(null);
  const latest = useRef(value);
  useEffect(() => {
    latest.current = value;
  }, [value]);
  if (!holding || holding.status !== "drafted") return null;
  const mismatch = holdingDayNote(value, holding.phrase);
  const note = mismatch ? (keptNote ?? mismatch) : null;

  const pick = async (day: string) => {
    if (day === holding.day || busy) return;
    setBusy(true);
    setKeptNote(null);
    const submitted = value;
    try {
      const res = await unstickApi.changeHoldingDay(cardId, day, submitted);
      acceptCard(queryClient, res.card);
      queryClient.setQueryData(unstickKeys.holding(cardId), { holding: res.holding });
      const next = res.card.blocks.find((b) => b.id === HOLDING_BLOCK_ID)?.value;
      const after = afterDayChange(submitted, latest.current, next);
      if (after.text !== null) onChange(after.text);
      setKeptNote(after.note);
    } catch (error) {
      toast(
        isApiError(error, "invalid_request") && error.serverMessage
          ? `${error.serverMessage}.`
          : "Couldn't change the day just now. Your text is kept.",
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const res = await unstickApi.removeHolding(cardId);
      acceptCard(queryClient, res.card);
      queryClient.setQueryData(unstickKeys.holding(cardId), { holding: null });
      toast("Holding reply taken off this card.");
    } catch {
      toast("Couldn't take it off just now. Nothing changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5" data-testid="holding-day-picker">
      <span className="text-sm text-muted">Reply by</span>
      <div role="radiogroup" aria-label="Reply by" className="flex flex-wrap gap-1">
        {holding.choices.map((c) => (
          <button
            key={c.day}
            type="button"
            role="radio"
            aria-checked={c.day === holding.day}
            disabled={readOnly || busy}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => void pick(c.day)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs",
              c.day === holding.day
                ? "border-accent bg-accent-soft font-medium text-accent-text"
                : "border-line text-ink-2 hover:bg-sunk hover:text-ink",
            )}
          >
            {c.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={readOnly || busy}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => void remove()}
        className="ml-auto inline-flex items-center gap-1 text-xs text-muted hover:text-ink"
      >
        <X aria-hidden className="size-3.5" />
        Not now
      </button>
      {note ? (
        <p className="basis-full text-xs text-ink-2" role="status" data-testid="holding-day-note">
          {note}
        </p>
      ) : null}
    </div>
  );
}
